import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { AirdropEligibilityBreakdownProps } from '../components/airdrop-eligibility-breakdown/AirdropEligibilityBreakdown'
import { AirdropBreakdownQueryData } from './airdropBreakdownQuery'
import { AirdropClaimsQueryData } from './airdropClaimsQuery'

export type AllocationsSummary = {
  airdrops: AirdropEligibilityBreakdownProps['airdrops']
  totalAllocation: NormalizedNumber
  ignitionAllocation: NormalizedNumber
  overdriveAllocation: NormalizedNumber
} | null

export function getAllocationsSummary(
  airdropBreakdown: AirdropBreakdownQueryData,
  claims?: AirdropClaimsQueryData,
): AllocationsSummary {
  if (!airdropBreakdown) {
    return null
  }

  const { allocations } = airdropBreakdown
  const {
    ignition: ignitionClaimed,
    preFarmingAndSocial: preFarmingAndSocialClaimed,
    cookie3: cookie3Claimed,
    overdrive: overdriveClaimed,
  } = claims ?? {}

  const totalAllocation = allocations.reduce((acc, curr) => acc.plus(curr.amount_normalized), NormalizedNumber.ZERO)
  const ignitionAllocation =
    allocations.find((item) => item.type === 'spark-ignition')?.amount_normalized ?? NormalizedNumber.ZERO
  const preFarmingAllocation =
    allocations
      .find((item) => item.type === 'pre-farming-and-social')
      ?.groups.filter((group) => group.tag === AAVE_PREFARMING_TAG || group.tag === SPARKLEND_PREFARMING_TAG)
      .reduce((acc, curr) => acc.plus(curr.value), NormalizedNumber.ZERO) ?? NormalizedNumber.ZERO
  const socialAllocation =
    allocations
      .find((item) => item.type === 'pre-farming-and-social')
      ?.groups.find((group) => group.tag === SOCIAL_CAMPAIGN_TAG)?.value ?? NormalizedNumber.ZERO
  const cookie3Allocation =
    allocations.find((item) => item.type === 'cookie3')?.amount_normalized ?? NormalizedNumber.ZERO
  const overdriveAllocation =
    allocations.find((item) => item.type === 'overdrive')?.amount_normalized ?? NormalizedNumber.ZERO

  const airdrops: AirdropEligibilityBreakdownProps['airdrops'] = [
    {
      origin: 'spark-ignition',
      amount: ignitionAllocation,
      status: getAirdropStatus(ignitionAllocation, ignitionClaimed),
      detailedBreakdown:
        allocations
          .find((item) => item.type === 'spark-ignition')
          ?.groups.filter((group) => group.value.gt(0))
          .sort((a, b) => b.value.minus(a.value).toNumber())
          .map((group) => ({
            label: TAG_TO_LABEL[group.tag] ?? group.tag,
            amount: group.value,
          })) ?? [],
    },
    {
      origin: 'pre-farming',
      amount: preFarmingAllocation,
      status: getAirdropStatus(preFarmingAllocation, preFarmingAndSocialClaimed),
      detailedBreakdown:
        allocations
          .find((item) => item.type === 'pre-farming-and-social')
          ?.groups.filter((group) => group.tag === AAVE_PREFARMING_TAG || group.tag === SPARKLEND_PREFARMING_TAG)
          .sort((a, b) => b.value.minus(a.value).toNumber())
          .map((group) => ({
            label: TAG_TO_LABEL[group.tag] ?? group.tag,
            amount: group.value,
          })) ?? [],
    },
    {
      origin: 'social-campaign',
      amount: socialAllocation,
      status: getAirdropStatus(socialAllocation, preFarmingAndSocialClaimed),
    },
    {
      origin: 'cookie3',
      amount: cookie3Allocation,
      status: getAirdropStatus(cookie3Allocation, cookie3Claimed),
    },
    {
      origin: 'spark-overdrive',
      amount: overdriveAllocation,
      status: getAirdropStatus(overdriveAllocation, overdriveClaimed),
    },
  ]

  return {
    airdrops,
    totalAllocation,
    ignitionAllocation,
    overdriveAllocation,
  }
}

const AAVE_PREFARMING_TAG = 'aave_reward'
const SPARKLEND_PREFARMING_TAG = 'sparklend_reward'
const SOCIAL_CAMPAIGN_TAG = 'layer3_reward'

const TAG_TO_LABEL: Record<string, string> = {
  sDAI: 'sDAI holder',
  sparklend: 'Deposited into SparkLend',
  USDS: 'USDS holder',
  SAI: 'SAI holder',
  sUSDS: 'sUSDS holder',
  curve: 'Curve 3pool liquidity provider',
  aave: 'Aave stablecoin lender',
  usde: 'Ethena USDe holder',
  sUSDC: 'sUSDC holder',
  xDAI: 'xDAI holder',
  pendle: 'Pendle PT, YT, and/or LP holder in top 3 markets by TVL',
  susde: 'Ethena sUSDe holder',
  morpho: 'Morpho stablecoin lender',
  DAI: 'DAI holder',
  fluid: 'Fluid stablecoin lender',
  vePendle: 'Pendle vePENDLE holder',
  [AAVE_PREFARMING_TAG]: 'Aave users',
  [SPARKLEND_PREFARMING_TAG]: 'SparkLend users',
}

function getAirdropStatus(
  allocation: NormalizedNumber,
  claimed?: NormalizedNumber,
): 'eligible' | 'ineligible' | 'claimed' | 'expired' {
  if (allocation.eq(0)) {
    return 'ineligible'
  }

  if (!claimed) {
    return 'eligible'
  }

  if (claimed.gt(0)) {
    return 'claimed'
  }

  return 'expired'
}
